import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import {
  EditorFranjasResponse,
  EstadoFranjasResponse,
  HistorialFranja,
  MainRatesService,
  MotorTasasVes,
  ParFranja,
  PrevisualizarFranjasResponse,
  RangoMargenVes,
  TasaFranja,
} from '../../services/main-rates.service';
import * as F from './franjas-editor.rules';
import * as MV from './margenes-ves.rules';
import {
  LeidosTramosUsdt,
  ParTramosUsdt,
  REUSO_PESTANA_MS,
  TramosUsdtEditorComponent,
} from './tramos-usdt-editor.component';

/** Lo que se avisa al guardar con éxito, para refrescar las tarjetas. */
export interface FranjasGuardadas {
  /** 'VES-USDT' / 'USDT-VES' = las pestañas de tramos USDT. */
  par: ParFranja | ParTramosUsdt;
  cambios: number;
  aplicado: boolean;
  /**
   * Sin aplicar porque el motor no grabó (el back dice el motivo): no hay
   * nada que esperar del cron. Null/ausente con `aplicado: false` = el lock
   * estaba ocupado y se grabarán en unos segundos (se espera, como siempre).
   */
  motivoNoAplicado?: string | null;
  /** Huella de la tarjeta leída justo ANTES del PUT (ver `FranjasEditorData.leerFoto`). */
  foto?: string;
  /** Tasas que la vista previa oficial dice que se van a grabar. */
  esperadas?: F.TasaEsperada[];
}

/**
 * Paso del guardado para la línea «Guardado → Aplicando… → Aplicado a las HH:MM».
 * `se-aplicara`: la espera al cron de main-rates se agotó (~2 min) sin verlo.
 */
export type PasoAplicacion = 'guardado' | 'aplicando' | 'aplicado' | 'se-aplicara';

/** Estado de aplicación de lo último guardado. Lo escriben el editor y main-rates. */
export interface EstadoAplicacion {
  par: ParFranja | ParTramosUsdt;
  paso: PasoAplicacion;
  /** Cuándo se vio aplicado (para «Aplicado a las HH:MM»); null si todavía no. */
  en: Date | null;
}

/** Lo que devuelve el modal al cerrarse pidiendo el modal de comisión VES→USDT. */
export const RESULTADO_ABRIR_COMISION = 'abrir-comision-ves-usdt';

/** Datos con los que main-rates abre el modal. */
export interface FranjasEditorData {
  puedeEditar: boolean;
  /** Pestaña con la que abre. Por defecto CLP-VES. */
  par?: ParFranja | ParTramosUsdt;
  /**
   * Lectura FRESCA de la tarjeta del par (su huella), que el editor pide justo
   * antes del PUT. main-rates la usa para saber cuándo el cron ya grabó.
   */
  leerFoto?: (par: ParFranja) => Promise<string>;
  /**
   * `rates/franjas/estado` recién leído por el panel (menos de 30 s): el
   * editor lo usa en vez de volver a pedirlo al abrir.
   */
  estadoFranjas?: EstadoFranjasResponse;
}

/** Respuesta de la vista previa o el error que dio, para el stream en vivo. */
type ResultadoVista =
  | { par: ParFranja; ok: true; r: PrevisualizarFranjasResponse }
  | { par: ParFranja; ok: false; errores: string[] };

/**
 * Editor de franjas CLP↔VES (modal).
 *
 * Fórmula de siempre (modo DESCUENTOS): cada franja lleva una cantidad entera
 * de descuentos, automática por posición (N-1…0) y editable a mano. El «Desde»
 * de la primera franja es el monto mínimo y el «Hasta» de la última es el
 * máximo; los cortes del medio se encadenan solos, así no queda hueco ni cruce.
 * Todo en vivo: barra, validación por campo y vista previa oficial del back
 * con debounce. El back valida y sus errores y advertencias se muestran siempre.
 *
 * Se abre con MatDialog, como el resto de la pantalla de tasas. Se cierra con
 * la X, Cancelar, Esc o clic fuera; si hay cambios sin guardar pide
 * confirmación, y mientras guarda no se puede cerrar ni editar.
 */
@Component({
  selector: 'app-franjas-editor',
  templateUrl: './franjas-editor.component.html',
  styleUrls: ['./franjas-editor.component.sass'],
})
export class FranjasEditorComponent implements OnInit, OnDestroy {
  /** Mismo candado de escritura que el resto de la pantalla de tasas. */
  @Input() puedeEditar = false;

  /** Se emite al guardar con éxito, para recargar las tarjetas «Tasa web del día». */
  @Output() guardado = new EventEmitter<FranjasGuardadas>();

  /**
   * Estado de aplicación de lo último guardado (línea de 3 pasos). El editor
   * lo pone al guardar; si quedó «aplicando», main-rates lo actualiza desde
   * su espera al cron (`aplicando` → `aplicado` / `se-aplicara`).
   */
  estadoAplicacion: EstadoAplicacion | null = null;

  /** Espera después del último cambio antes de pedir la vista previa oficial. */
  static readonly DEBOUNCE_MS = 700;

  readonly motivoMax = F.MOTIVO_MAX;
  readonly ayudaDescuentos = F.AYUDA_DESCUENTOS;
  readonly textoNegativa = F.TEXTO_NEGATIVA;
  readonly descuentosMax = F.DESCUENTOS_MAX;
  readonly montoEjemplo = F.MONTO_EJEMPLO;
  readonly textoSoloLectura = F.TEXTO_SOLO_LECTURA;
  readonly textoPasaTope = F.TEXTO_PASA_TOPE_VES_CLP;
  readonly textoRepartoAutomatico = MV.TEXTO_REPARTO_AUTOMATICO;
  readonly pares: { par: ParFranja; etiqueta: string }[] = [
    { par: 'CLP-VES', etiqueta: 'CLP → VES' },
    { par: 'VES-CLP', etiqueta: 'VES → CLP' },
  ];

  par: ParFranja = 'CLP-VES';

  /** Pestaña VES → USDT activa: cuerpo y pie los pone `app-tramos-usdt-editor`. */
  usdt = false;
  /** Cuál de las dos pestañas de tramos USDT está activa (cuando `usdt`). */
  parUsdt: ParTramosUsdt = 'VES-USDT';
  @ViewChild(TramosUsdtEditorComponent) tramosUsdt?: TramosUsdtEditorComponent;

  cargando = false;
  errorCarga: string | null = null;
  estado: EstadoFranjasResponse | null = null;
  editor: EditorFranjasResponse | null = null;
  /** Cuándo se leyó `estado` (es el mismo para los dos pares). */
  private estadoEn = 0;
  /** Editor de cada par leído en este modal, para volver a su pestaña sin pedirlo. */
  private leidos = new Map<ParFranja, { en: number; r: EditorFranjasResponse }>();

  /** Lo que se cargó del servidor, para saber si hay cambios. */
  private inicial: F.Borrador | null = null;
  /**
   * Versión de lo cargado (si el back la manda). Viaja en el PUT: si otro
   * operador guardó antes, el back responde 409 y se ofrece recargar.
   */
  private version: string | null = null;
  borrador: F.Borrador | null = null;

  /** Barra de franjas, recalculada en cada cambio. */
  segmentos: F.SegmentoBarra[] = [];
  /** Lo que cambia frente a lo guardado. */
  cambiosLista: string[] = [];

  /** Monto del simulador, en la moneda que paga el cliente. */
  simMonto: number | null = F.MONTO_EJEMPLO;

  /** Error de una acción local (agregar, quitar). */
  errorAccion: string | null = null;

  motivo = '';
  previsualizando = false;
  guardando = false;
  vista: PrevisualizarFranjasResponse | null = null;
  /**
   * La vista previa quedó vieja: se editó algo y la nueva todavía no llegó
   * (esperando el debounce o en vuelo). Mientras tanto no se puede guardar.
   */
  vistaVieja = false;
  /**
   * La última vista previa buena del par. En MARGENES, si la siguiente falla
   * (o no sale por un error local), la tabla sigue mostrándola como pendiente
   * en vez de quedar en blanco.
   */
  private ultimaVista: PrevisualizarFranjasResponse | null = null;
  errores: string[] = [];
  advertencias: string[] = [];
  mensaje: string | null = null;
  /** El guardado no grabó las tasas (`motivoNoAplicado`): el mensaje va en ámbar, no en verde. */
  mensajeAviso = false;

  // Historial: plegado por defecto, 5 por página.
  historialAbierto = false;
  historialPagina = 1;
  historialItems: HistorialFranja[] = [];
  historialTotal = 0;
  cargandoHistorial = false;
  errorHistorial: string | null = null;
  private subHistorial?: Subscription;

  /** Cada cambio del borrador; la vista previa oficial sale de acá con debounce. */
  private cambios$ = new Subject<{ inmediato: boolean }>();
  private subVista: Subscription;
  private subCierre = new Subscription();

  constructor(
    private mainRates: MainRatesService,
    @Optional() private dialogRef: MatDialogRef<FranjasEditorComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: FranjasEditorData | null,
  ) {
    if (this.data) this.puedeEditar = !!this.data.puedeEditar;
    if (
      this.data?.par === 'VES-USDT' ||
      this.data?.par === 'USDT-VES' ||
      this.data?.par === 'CLP-USDT' ||
      this.data?.par === 'USDT-CLP'
    ) {
      this.usdt = true;
      this.parUsdt = this.data.par;
    } else if (this.data?.par) this.par = this.data.par;
    this.subVista = this.cambios$
      .pipe(
        // Al escribir espera DEBOUNCE_MS sin cambios; `inmediato` (recién
        // cargado, Actualizar, Descartar) va ya. Cada cambio nuevo cancela la
        // espera Y la vista previa en vuelo: nunca llega una de un borrador
        // viejo. (Antes `of(c).pipe(debounceTime)` emitía al completar, o sea
        // en cada tecla: un POST previsualizar por tecla.)
        switchMap((c) =>
          (c.inmediato ? of(0) : timer(FranjasEditorComponent.DEBOUNCE_MS)).pipe(switchMap(() => this.pedirVista())),
        ),
      )
      .subscribe((res) => this.aplicarVista(res));
  }

  ngOnInit(): void {
    if (this.dialogRef) {
      // El cierre lo decide el editor: confirma si hay cambios y no deja
      // cerrar mientras guarda.
      this.dialogRef.disableClose = true;
      this.subCierre.add(this.dialogRef.backdropClick().subscribe(() => this.cerrar()));
      this.subCierre.add(
        this.dialogRef
          .keydownEvents()
          .pipe(filter((e) => e.key === 'Escape'))
          .subscribe(() => this.cerrar()),
      );
    }
    if (this.data?.estadoFranjas) {
      this.estado = this.data.estadoFranjas;
      this.estadoEn = Date.now();
    }
    if (!this.usdt) this.cargar(false, true);
  }

  /** Lo de este modal todavía sirve (ver `REUSO_PESTANA_MS`). */
  private reciente(en: number): boolean {
    return en > 0 && Date.now() - en < REUSO_PESTANA_MS;
  }

  /**
   * Pestañas de tramos USDT: lo que cada una leyó en este modal, para no
   * pedirlo de nuevo al volver a ella (lo usa `app-tramos-usdt-editor`).
   */
  readonly leidosUsdt: LeidosTramosUsdt = new Map();

  ngOnDestroy(): void {
    this.subVista?.unsubscribe();
    this.subCierre.unsubscribe();
    this.subHistorial?.unsubscribe();
  }

  /** Confirmación con el mismo Swal que usa el resto del panel. */
  private async confirmarDescarte(texto: string): Promise<boolean> {
    const r = await Swal.fire({
      title: 'Hay cambios sin guardar',
      text: texto,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Descartar cambios',
      cancelButtonText: 'Seguir editando',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true,
    });
    return !!r.isConfirmed;
  }

  /** X, Cancelar, Esc o clic fuera. */
  async cerrar(): Promise<void> {
    if (this.bloqueado) return;
    if (this.cambiosPestana && !(await this.confirmarDescarte('Si cierras, se pierden.'))) return;
    this.dialogRef?.close();
  }

  /**
   * «Cambiar comisión» de la pestaña VES → USDT: la comisión vive en su modal
   * de siempre. Si hay tramos sin guardar se confirma antes; después se cierra
   * este modal y main-rates abre el de comisión.
   */
  async pedirComision(): Promise<void> {
    if (this.bloqueado) return;
    if (
      this.cambiosPestana &&
      !(await this.confirmarDescarte('Para cambiar la comisión se cierra este editor y se pierden los cambios de los tramos.'))
    ) {
      return;
    }
    this.dialogRef?.close(RESULTADO_ABRIR_COMISION);
  }

  /** Pestaña activa, sea de franjas o de tramos USDT. */
  get parActivo(): ParFranja | ParTramosUsdt {
    return this.usdt ? this.parUsdt : this.par;
  }

  /** Quien no edita tasas abre el editor igual, en solo lectura. */
  get soloLectura(): boolean {
    return !this.puedeEditar;
  }

  /** Estado de aplicación de la pestaña activa (línea de 3 pasos); null si no aplica. */
  get pasoVisible(): EstadoAplicacion | null {
    const e = this.estadoAplicacion;
    return e && e.par === this.parActivo ? e : null;
  }

  /** «Aplicado a las HH:MM» (o solo «Aplicado» si no hay hora). */
  get textoAplicado(): string {
    const hora = F.horaCorta(this.estadoAplicacion?.en ?? null);
    return hora ? `Aplicado a las ${hora}` : 'Aplicado';
  }

  /** Guardando en cualquiera de las pestañas: no se cierra ni se cambia. */
  get bloqueado(): boolean {
    return this.guardando || !!this.tramosUsdt?.guardando;
  }

  /** Cambios sin guardar de la pestaña activa. */
  get cambiosPestana(): boolean {
    return this.usdt ? !!this.tramosUsdt?.hayCambios : this.hayCambios;
  }

  /** La pestaña VES → USDT reenvía su guardado con la misma forma. */
  alGuardarUsdt(e: FranjasGuardadas): void {
    // Los tramos se aplican al guardar: la línea queda en «Aplicado».
    if (e && e.cambios > 0) this.estadoAplicacion = { par: e.par, paso: 'aplicado', en: new Date() };
    this.guardado.emit(e);
  }

  /** La pestaña de tramos se editó: el estado del guardado anterior ya no aplica. */
  alEditarUsdt(): void {
    this.estadoAplicacion = null;
  }

  /** Pestaña de tramos USDT: VES→USDT (por defecto), USDT→VES, CLP→USDT o USDT→CLP. */
  async abrirUsdt(par: ParTramosUsdt = 'VES-USDT'): Promise<void> {
    if ((this.usdt && this.parUsdt === par) || this.bloqueado) return;
    if (
      this.cambiosPestana &&
      !(await this.confirmarDescarte('Si cambias de par, se pierden los cambios de este.'))
    ) {
      return;
    }
    this.usdt = true;
    this.parUsdt = par;
    this.estadoAplicacion = null;
  }

  async cambiarPar(par: ParFranja): Promise<void> {
    if ((!this.usdt && par === this.par) || this.cargando || this.bloqueado) return;
    if (
      this.cambiosPestana &&
      !(await this.confirmarDescarte('Si cambias de par, se pierden los cambios de este.'))
    ) {
      return;
    }
    this.usdt = false;
    this.par = par;
    this.estadoAplicacion = null;
    this.simMonto = F.MONTO_EJEMPLO;
    this.cargar(false, true);
  }

  /**
   * `conservarMensaje`: al recargar después de guardar no se borra el aviso.
   * `reusar`: abrir o cambiar de pestaña usa lo leído en este modal hace menos
   * de `REUSO_PESTANA_MS` (estado y editor del par) en vez de pedirlo otra vez.
   */
  cargar(conservarMensaje = false, reusar = false): void {
    this.cargando = true;
    this.errorCarga = null;
    const mensaje = this.mensaje;
    const mensajeAviso = this.mensajeAviso;
    const advertencias = this.advertencias;
    this.limpiarResultados();
    if (conservarMensaje) {
      this.mensaje = mensaje;
      this.mensajeAviso = mensajeAviso;
      this.advertencias = advertencias;
    }
    this.errorAccion = null;
    const par = this.par;

    if (!reusar || !this.reciente(this.estadoEn)) {
      this.mainRates.obtenerEstadoFranjas().subscribe({
        next: (e) => {
          this.estado = e;
          this.estadoEn = Date.now();
        },
        error: () => {
          this.estado = null;
          this.estadoEn = 0;
        },
      });
    }

    const previo = reusar ? this.leidos.get(par) : undefined;
    if (previo && this.reciente(previo.en)) {
      this.aplicarEditor(par, previo.r);
      return;
    }

    this.mainRates.obtenerEditorFranjas(par).subscribe({
      next: (r) => {
        this.leidos.set(par, { en: Date.now(), r });
        this.aplicarEditor(par, r);
      },
      error: (err) => {
        if (par !== this.par) return;
        this.editor = null;
        this.ultimaVista = null;
        this.version = null;
        this.inicial = null;
        this.borrador = null;
        this.segmentos = [];
        this.cambiosLista = [];
        const status = F.statusDe(err);
        this.errorCarga =
          status === 404 || status === 503
            ? 'El editor de franjas no está disponible en este momento.'
            : F.erroresDe(err).join(' ');
        this.cargando = false;
      },
    });
  }

  /** Pinta el editor del par (recién leído o reusado de este modal). */
  private aplicarEditor(par: ParFranja, r: EditorFranjasResponse): void {
    if (par !== this.par) return;
    this.editor = r;
    this.ultimaVista = null;
    this.version = F.versionDe(r);
    this.inicial = F.borradorDesde(r?.franjas);
    this.borrador = F.clonar(this.inicial);
    this.motivo = '';
    this.cargando = false;
    this.reiniciarHistorial(r);
    this.recalcular();
    // Vista previa oficial de entrada: deja lista la tasa oficial.
    this.cambios$.next({ inmediato: true });
  }

  // ───────────────────────── Lectura ─────────────────────────

  get infoPar() {
    return this.estado?.pares?.find((p) => p.par === this.par) ?? null;
  }

  /** La tabla no se pudo usar y el backend cayó a los tramos de siempre. */
  get rigeConstante(): boolean {
    return this.infoPar?.fuente === 'CONSTANTE';
  }

  // ───────────────────────── Motor de márgenes (03/11/2026) ─────────────────────────
  //
  // Con el motor MARGENES el margen de cada franja es automático (se reparte
  // entre el mínimo y el máximo de «Actualizar Parámetros») y de solo lectura:
  // acá se editan solo los cortes, agregar/quitar y el motivo. La tasa y el
  // margen salen siempre de la vista previa del back (nada de la cuenta de
  // descuentos del navegador). En LEGADO todo sigue como siempre.

  /** Motor del par: el de la última vista previa o, si no, el del GET. Sin dato (back viejo), LEGADO. */
  get motor(): MotorTasasVes {
    return MV.motorDe(this.vista?.motor) ?? MV.motorDe(this.editor?.motor) ?? 'LEGADO';
  }

  get enMargenes(): boolean {
    return this.motor === 'MARGENES';
  }

  /** Márgenes del par (de la vista previa si los trae; si no, del GET). `undefined` = el back no los manda. */
  get margenesPar(): RangoMargenVes | null | undefined {
    if (this.vista && this.vista.margenes !== undefined) return this.vista.margenes;
    return this.editor?.margenes;
  }

  /** MARGENES sin los márgenes del par en la BD: el back avisa y acá no se deja guardar. */
  get faltanMargenes(): boolean {
    return this.enMargenes && this.margenesPar === null;
  }

  get textoFaltanMargenes(): string {
    return MV.textoFaltanMargenes(this.par);
  }

  /** Spot cruzado del par: CLP-VES en CLP por 1 VES; VES-CLP en VES por 1 CLP. */
  get spotCruzado(): number | null {
    const v = this.vista?.spotCruzado ?? this.editor?.spotCruzado;
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  }

  get unidadSpot(): string {
    return MV.unidadRuta(this.par);
  }

  /** Texto del aviso del 5 % de VES→CLP: en MARGENES se baja el margen máximo, no un descuento. */
  get textoTope(): string {
    return this.enMargenes ? MV.TEXTO_PASA_TOPE_MARGENES : this.textoPasaTope;
  }

  /**
   * MARGENES: la tasa oficial que se muestra en la fila `i` y si está al día.
   * - Vista previa al día: la suya (por su «desde»).
   * - Si no (esperando el debounce, en vuelo, o la última falló o no salió por
   *   un error local): la de la vista previa que haya o la última buena, por
   *   su «desde»; y si no está, la de `editor.tasas` cuando la franja no
   *   cambió frente a lo guardado. Siempre como pendiente.
   * Nunca la cuenta de descuentos del navegador: en MARGENES no aplica.
   */
  filaMargenes(i: number): { t: TasaFranja | null; alDia: boolean } {
    if (this.oficialAlDia) {
      const t = this.oficial(i);
      if (t) return { t, alDia: true };
    }
    const d = this.desde(i);
    const ref = this.vista ?? this.ultimaVista;
    const deRef = d != null && ref ? (ref.tasas || []).find((x) => x.desde === d) ?? null : null;
    if (deRef) return { t: deRef, alDia: false };
    const clave = this.filas[i]?.clave;
    return { t: this.franjaSinCambios(i) ? F.tasaDeClave(this.editor?.tasas, clave) : null, alDia: false };
  }

  /** La franja `i` ya existía y tiene el mismo rango que lo guardado. */
  private franjaSinCambios(i: number): boolean {
    const clave = this.filas[i]?.clave;
    if (!clave || !this.inicial) return false;
    const j = this.inicial.filas.findIndex((x) => x.clave === clave);
    return j >= 0 && F.desdeDe(this.inicial, j) === this.desde(i) && F.hastaDe(this.inicial, j) === this.hasta(i);
  }

  /** La fila muestra la tasa oficial al día (si no, lleva el reloj de «pendiente»). */
  filaAlDia(i: number): boolean {
    return this.enMargenes ? this.filaMargenes(i).alDia : this.tasaUsada(i).oficial;
  }

  /** Margen aplicado (automático) de la fila `i`, de la vista previa oficial (o la última, pendiente). */
  margenAplicado(i: number): number | null {
    const m = this.filaMargenes(i).t?.margenAplicado;
    return m != null && Number.isFinite(Number(m)) ? Number(m) : null;
  }

  /** La tasa de la fila `i` en la unidad de la ruta («1 VES = x CLP» / «1 CLP = x VES»). */
  textoTasaRuta(i: number): string {
    return MV.textoTasaRuta(this.par, this.filaMargenes(i).t?.tasaRuta);
  }

  textoTasaCliente(i: number): string {
    return MV.textoTasaCliente(this.tasaUsada(i).tasa);
  }

  formatoMargen(x: number | null | undefined): string {
    return MV.formatoMargen(x);
  }

  formatoTasaVes(x: number | null | undefined): string {
    return MV.formatoTasaVes(x);
  }

  get filas(): F.FilaDescuentos[] {
    return this.borrador?.filas ?? [];
  }

  get hayCambios(): boolean {
    return F.hayCambios(this.borrador, this.inicial);
  }

  get motivoValido(): boolean {
    return F.motivoValido(this.motivo);
  }

  get erroresLocales(): string[] {
    return this.borrador ? F.erroresBorrador(this.borrador) : [];
  }

  get todasAutomaticas(): boolean {
    const b = this.borrador;
    return !!b && b.filas.every((_, i) => F.esAutomatico(b, i));
  }

  /** Además: con la vista previa vieja (se editó y la nueva no llegó) no se guarda. */
  get puedeGuardar(): boolean {
    return (
      !!this.editor &&
      !this.faltanMargenes &&
      !this.vistaVieja &&
      F.puedeGuardar({
        puedeEditar: this.puedeEditar,
        hayCambios: this.hayCambios,
        motivoValido: this.motivoValido,
        errores: this.erroresLocales.length,
        guardando: this.guardando,
        previsualizando: this.previsualizando,
      })
    );
  }

  /** Por qué Guardar está deshabilitado, para el tooltip del botón. */
  get motivoNoGuardar(): string {
    if (!this.puedeEditar) return 'Tu usuario no tiene permisos para modificar las tasas.';
    if (this.faltanMargenes) return 'Faltan los márgenes de este par: complétalos en Actualizar Parámetros.';
    if (!this.hayCambios) return 'No hay cambios que guardar.';
    if (this.erroresLocales.length) return 'Corrige los campos marcados en rojo.';
    if (!this.motivoValido) return 'Escribe el motivo del cambio (5 a 255 caracteres).';
    if (this.previsualizando || this.vistaVieja) return 'Esperando la tasa oficial…';
    return '';
  }

  get editable(): boolean {
    return this.puedeEditar && !this.guardando;
  }

  /** Unidad de los montos: VES→CLP también se expresa en CLP equivalente. */
  get notaMontos(): string {
    return this.par === 'VES-CLP'
      ? 'Montos en CLP equivalentes al precio de mercado (el cliente paga en VES).'
      : 'Montos en CLP.';
  }

  desde(i: number): number | null {
    return this.borrador ? F.desdeDe(this.borrador, i) : null;
  }

  hasta(i: number): number | null {
    return this.borrador ? F.hastaDe(this.borrador, i) : null;
  }

  hastaVisible(i: number): number | null {
    return this.borrador ? F.hastaVisible(this.borrador, i) : null;
  }

  esUltima(i: number): boolean {
    return !!this.borrador && F.esUltima(this.borrador, i);
  }

  desdeEditable(i: number): boolean {
    return F.desdeEditable(i);
  }

  errorDesde(i: number): string | null {
    return this.borrador ? F.errorDesdeFila(this.borrador, i) : null;
  }

  errorHasta(i: number): string | null {
    return this.borrador ? F.errorHastaFila(this.borrador, i) : null;
  }

  errorDescuentos(f: F.FilaDescuentos): string | null {
    return F.errorDescuentos(f);
  }

  enVivo(f: F.FilaDescuentos): F.TasaEnVivo | null {
    return F.tasaEnVivo(
      this.par,
      this.editor?.base,
      this.editor?.insumos?.descuentoFiat,
      f.descuentos,
    );
  }

  /** Tasa oficial de la vista previa del back para la fila `i` (por su «desde»). */
  oficial(i: number): TasaFranja | null {
    const d = this.desde(i);
    if (!this.vista || d == null) return null;
    return (this.vista.tasas || []).find((t) => t.desde === d) || null;
  }

  /** ¿La tasa oficial de la vista previa está al día? */
  get oficialAlDia(): boolean {
    return !!this.vista && !this.vistaVieja && !this.previsualizando;
  }

  /**
   * Tasa client a mostrar: oficial si está al día, si no la del navegador.
   * En MARGENES no hay cuenta del navegador (la de descuentos no aplica): la
   * última oficial de la franja (`filaMargenes`), marcada como pendiente
   * hasta la vista previa.
   */
  tasaUsada(i: number): { tasa: number | null; oficial: boolean } {
    if (this.enMargenes) {
      const fm = this.filaMargenes(i);
      return { tasa: fm.t?.client ?? null, oficial: fm.alDia && fm.t?.client != null };
    }
    const of = this.oficialAlDia ? this.oficial(i)?.client : null;
    if (of != null) return { tasa: of, oficial: true };
    const f = this.filas[i];
    return { tasa: f ? this.enVivo(f)?.client ?? null : null, oficial: false };
  }

  /** Tooltip de la tasa: admin, oficial y la del navegador (en MARGENES, sin la del navegador). */
  detalleTasa(i: number): string {
    const f = this.filas[i];
    const of = this.oficial(i);
    const fmt = (x: number | null | undefined) =>
      x == null ? '—' : Number(x).toLocaleString('es-CL', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    if (this.enMargenes) {
      const fm = this.filaMargenes(i);
      return [
        `Admin ${fmt(fm.t?.admin)}`,
        `Oficial ${fm.alDia && fm.t ? fmt(fm.t.client) : 'pendiente'}`,
      ].join(' · ');
    }
    const vivo = f ? this.enVivo(f) : null;
    const partes = [
      `Admin ${fmt(this.oficialAlDia && of ? of.admin : vivo?.admin)}`,
      `Oficial ${this.oficialAlDia && of ? fmt(of.client) : 'pendiente'}`,
      `Navegador ${fmt(vivo?.client)}`,
    ];
    return partes.join(' · ');
  }

  /** Ganancia vs mercado (la oficial si está al día). En MARGENES, la de `filaMargenes` (sin estimar descuentos). */
  ganancia(i: number): number | null {
    if (this.enMargenes) {
      const m = this.filaMargenes(i).t?.margenPct;
      return m != null && Number.isFinite(Number(m)) ? Number(m) : null;
    }
    const of = this.oficialAlDia ? this.oficial(i) : null;
    if (of && Number.isFinite(Number(of.margenPct))) return Number(of.margenPct);
    const f = this.filas[i];
    const vivo = f ? this.enVivo(f)?.client : null;
    if (vivo != null) return F.gananciaVsMercado(this.par, vivo, this.editor?.mid);
    const actual = f ? F.tasaDeClave(this.editor?.tasas, f.clave) : null;
    return actual ? actual.margenPct : null;
  }

  negativa(i: number): boolean {
    const g = this.ganancia(i);
    return g != null && g < 0;
  }

  /** Recibe de menos frente a la base, en % (negativo); null si no se puede. */
  descuentoBase(i: number): number | null {
    return F.descuentoVsBase(this.par, this.tasaUsada(i).tasa, this.editor?.base);
  }

  /** VES→CLP: la ganancia llega al tope del aviso (fila en rojo, no bloquea). */
  pasaTope(i: number): boolean {
    return F.pasaTopeVesClp(this.par, this.ganancia(i));
  }

  /** Número en es-CL con `dec` decimales. */
  n(x: number | null | undefined, dec = 2): string {
    return F.formatoNumero(x, dec);
  }

  /** Porcentaje «1,25 %» (ver `formatoPct`). */
  pct(x: number | null | undefined, signo = false, ceroGuion = false): string {
    return F.formatoPct(x, { signo, ceroGuion });
  }

  pastilla(i: number): F.Pastilla | null {
    return F.pastillaEjemplo(this.par, this.tasaUsada(i).tasa);
  }

  esAutomatico(i: number): boolean {
    return !!this.borrador && F.esAutomatico(this.borrador, i);
  }

  get monedas() {
    return F.monedasDe(this.par);
  }

  get simulacion(): F.Simulacion | null {
    if (!this.borrador) return null;
    return F.simular(this.par, this.borrador, this.simMonto, this.editor?.mid);
  }

  /** Fila (y segmento) que resalta el simulador; -1 si ninguna. */
  get filaSimulada(): number {
    return this.simulacion?.indice ?? -1;
  }

  get simTasa(): { tasa: number | null; oficial: boolean } | null {
    const i = this.filaSimulada;
    return i >= 0 ? this.tasaUsada(i) : null;
  }

  get simRecibe(): string {
    const v = F.recibeCon(this.par, this.simTasa?.tasa, Number(this.simMonto));
    return v == null ? '—' : `${F.formatoMonto(v)} ${this.monedas.recibe}`;
  }

  formatoMonto(x: number | null | undefined): string {
    return F.formatoMonto(x);
  }

  etiquetaRango(desde: number | null, hasta: number | null): string {
    return F.etiquetaRangoClp(desde, hasta);
  }

  resumenCambio(h: any): string {
    return F.resumenCambio(h);
  }

  trackFila(i: number, f: F.FilaDescuentos): string {
    return f.clave || `nueva-${i}`;
  }

  trackIndice(i: number): number {
    return i;
  }

  // ───────────────────────── Edición ─────────────────────────

  /**
   * Cada cambio: barra, validación y resumen al instante; vista previa con
   * debounce. Hasta que llegue, la vista previa está vieja (no se guarda) y la
   * que estuviera en vuelo se cancela (ver el `switchMap` del constructor).
   */
  marcarEditado(): void {
    this.vistaVieja = true;
    this.previsualizando = false;
    this.mensaje = null;
    this.mensajeAviso = false;
    this.estadoAplicacion = null;
    this.recalcular();
    this.cambios$.next({ inmediato: false });
  }

  cambiarDesde(i: number, valor: any): void {
    if (!this.borrador || !F.desdeEditable(i)) return;
    this.borrador = F.cambiarDesdePrimera(this.borrador, valor);
    this.marcarEditado();
  }

  cambiarHasta(i: number, valor: any): void {
    if (!this.borrador) return;
    this.borrador = F.cambiarHasta(this.borrador, i, valor);
    this.marcarEditado();
  }

  cambiarDescuentos(f: F.FilaDescuentos, valor: any): void {
    f.descuentos = valor === '' || valor == null ? null : Number(valor);
    this.marcarEditado();
  }

  /** Vuelve a poner N-1…0 en todas las franjas activas. */
  automatico(): void {
    if (!this.borrador) return;
    this.borrador = F.aplicarAutomatico(this.borrador);
    this.marcarEditado();
  }

  /** En MARGENES los descuentos no rigen: la franja nueva va con 0 y las demás no se tocan. */
  agregar(): void {
    if (!this.borrador) return;
    const r = F.agregarAlFinal(this.borrador, { automatico: !this.enMargenes });
    if (r.error) {
      this.errorAccion = r.error;
      return;
    }
    this.errorAccion = null;
    this.borrador = r.borrador;
    this.marcarEditado();
  }

  quitar(i: number): void {
    if (!this.borrador) return;
    const r = F.quitar(this.borrador, i, this.editor?.franjas, { automatico: !this.enMargenes });
    if (r.error) {
      this.errorAccion = r.error;
      return;
    }
    this.errorAccion = null;
    this.borrador = r.borrador;
    this.marcarEditado();
  }

  descartar(): void {
    if (this.inicial) this.borrador = F.clonar(this.inicial);
    this.errorAccion = null;
    this.limpiarResultados();
    this.recalcular();
    this.cambios$.next({ inmediato: true });
  }

  private recalcular(): void {
    this.segmentos = this.borrador ? F.segmentosBarra(this.borrador) : [];
    // En MARGENES los descuentos no rigen: el resumen no los lista.
    this.cambiosLista = F.resumenCambios(this.borrador, this.inicial, { descuentos: !this.enMargenes });
  }

  // ───────────────────────── Historial ─────────────────────────

  get paginasHistorial(): number {
    return F.totalPaginas(this.historialTotal);
  }

  get hayPaginacion(): boolean {
    return F.mostrarPaginacion(this.historialTotal);
  }

  toggleHistorial(): void {
    this.historialAbierto = !this.historialAbierto;
  }

  /** Página 1 = los 5 que vienen con el editor. Las demás se piden al back. */
  irAPagina(pagina: number): void {
    const destino = F.paginaValida(pagina, this.historialTotal);
    if (destino === this.historialPagina || this.cargandoHistorial) return;
    if (destino === 1 && this.editor) {
      this.reiniciarHistorial(this.editor);
      return;
    }
    const par = this.par;
    this.cargandoHistorial = true;
    this.errorHistorial = null;
    this.subHistorial?.unsubscribe();
    this.subHistorial = this.mainRates
      .obtenerHistorialFranjas(par, destino, F.HISTORIAL_TAMANO)
      .subscribe({
        next: (r) => {
          this.cargandoHistorial = false;
          if (par !== this.par) return;
          this.historialItems = r?.items || [];
          if (Number.isFinite(Number(r?.total))) this.historialTotal = Number(r.total);
          this.historialPagina = Number(r?.pagina) || destino;
        },
        error: (err) => {
          this.cargandoHistorial = false;
          this.errorHistorial = F.erroresDe(err).join(' ');
        },
      });
  }

  private reiniciarHistorial(r: EditorFranjasResponse | null): void {
    this.subHistorial?.unsubscribe();
    this.cargandoHistorial = false;
    this.errorHistorial = null;
    this.historialPagina = 1;
    const items = r?.historial || [];
    this.historialItems = items.slice(0, F.HISTORIAL_TAMANO);
    const total = Number(r?.historialTotal);
    this.historialTotal = Number.isFinite(total) && total >= 0 ? total : items.length;
  }

  // ───────────────────────── Servidor ─────────────────────────

  /** Botón «Actualizar»: pide la vista previa oficial sin esperar. */
  previsualizar(): void {
    this.cambios$.next({ inmediato: true });
  }

  /**
   * Una vista previa del borrador actual. Con errores locales no se llama al
   * back. `switchMap` arriba cancela la anterior si llega otro cambio.
   */
  private pedirVista(): Observable<ResultadoVista | null> {
    if (!this.editor || !this.borrador) return of(null);
    const par = this.par;
    const locales = this.erroresLocales;
    if (locales.length) {
      this.previsualizando = false;
      return of({ par, ok: false, errores: locales } as ResultadoVista);
    }
    this.previsualizando = true;
    return this.mainRates.previsualizarFranjas(par, F.entradaDesdeBorrador(this.borrador)).pipe(
      map((r) => ({ par, ok: true, r } as ResultadoVista)),
      catchError((err) => of({ par, ok: false, errores: F.erroresDe(err) } as ResultadoVista)),
      tap(() => (this.previsualizando = false)),
    );
  }

  private aplicarVista(res: ResultadoVista | null): void {
    this.previsualizando = false;
    if (!res || res.par !== this.par) return;
    if (res.ok) {
      this.vista = res.r;
      this.vistaVieja = false;
      this.errores = res.r?.errores || [];
      this.advertencias = res.r?.advertencias || [];
      // La última buena (con tasas): MARGENES la sigue mostrando si la próxima falla.
      if (res.r?.ok !== false && Array.isArray(res.r?.tasas) && res.r.tasas.length) this.ultimaVista = res.r;
    } else {
      // Falló o no salió (error local): sin vista al día. En MARGENES la tabla
      // sigue con la última buena, marcada pendiente (`filaMargenes`).
      this.vista = null;
      this.vistaVieja = false;
      this.errores = res.errores;
      this.advertencias = [];
    }
  }

  async guardar(): Promise<void> {
    if (!this.puedeGuardar || !this.editor || !this.borrador) return;

    const confirmacion = await Swal.fire({
      title: '¿Guardar las franjas?',
      html:
        `<p style="margin-bottom:8px">Par <b>${this.par}</b>.</p>` +
        '<p style="color:#92400e">Las tasas nuevas se aplican al cliente en unos segundos.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (!confirmacion.isConfirmed) return;

    this.guardando = true;
    this.errores = [];
    this.advertencias = [];
    this.mensaje = null;
    const par = this.par;
    // Lo que se va a grabar (si la vista previa oficial está al día) y una
    // lectura fresca de la tarjeta antes del PUT: con eso main-rates sabe
    // cuándo el cron ya aplicó lo guardado.
    const esperadas = this.oficialAlDia ? F.esperadasDesdeVista(this.vista?.tasas) : undefined;
    let foto: string | undefined;
    if (this.data?.leerFoto) {
      foto = await this.data.leerFoto(par).catch(() => undefined);
    }

    this.mainRates
      .guardarFranjas(par, F.entradaDesdeBorrador(this.borrador), this.motivo.trim(), this.version)
      .subscribe({
        next: (r) => {
          this.guardando = false;
          const cambios = Number(r?.cambios) || 0;
          const aplicado = !!r?.aplicado;
          // Sin aplicar hay dos casos: el lock estaba ocupado (se grabarán en
          // unos segundos: «Aplicando…» y main-rates espera al cron, como
          // siempre) o el motor no grabó (`motivoNoAplicado`: no hay nada que
          // esperar; las tasas siguen las últimas).
          const motivoNoAplicado =
            !aplicado && typeof r?.motivoNoAplicado === 'string' && r.motivoNoAplicado.trim() ? r.motivoNoAplicado.trim() : null;
          if (cambios > 0) {
            this.estadoAplicacion = aplicado
              ? { par, paso: 'aplicado', en: new Date() }
              : motivoNoAplicado
                ? null
                : { par, paso: 'aplicando', en: null };
          }
          this.guardado.emit({ par, cambios, aplicado, motivoNoAplicado, foto, esperadas });
          if (par !== this.par) return;
          // El `mensaje` del back manda (ya dice qué se grabó y qué no). Si el
          // motor no grabó, va como aviso, no como éxito.
          const delBack = typeof r?.mensaje === 'string' && r.mensaje.trim() ? r.mensaje.trim() : null;
          const noGrabo = cambios > 0 && !!motivoNoAplicado;
          this.mensaje =
            delBack ??
            (noGrabo
              ? `Franjas guardadas, pero las tasas no se grabaron: ${motivoNoAplicado}. Siguen las últimas.`
              : cambios > 0 && !aplicado
                ? 'Franjas guardadas. Las tasas nuevas se grabarán en unos segundos.'
                : 'Franjas guardadas y aplicadas.');
          this.mensajeAviso = noGrabo;
          this.advertencias = r?.advertencias || [];
          this.aviso(noGrabo ? 'warning' : 'success', this.mensaje);
          this.cargar(true);
        },
        error: (err) => {
          this.guardando = false;
          const sinPermiso = F.mensajeSinPermiso(err);
          if (sinPermiso) {
            this.errores = [sinPermiso];
            this.avisoSinPermiso(sinPermiso);
            return;
          }
          // 409: otro operador guardó antes. Su mensaje y la opción de recargar.
          const conflicto = F.mensajeConflicto(err, 'franjas');
          if (conflicto) {
            this.errores = [conflicto];
            this.avisoConflicto(conflicto);
            return;
          }
          this.errores = F.erroresDe(err);
          this.aviso('error', 'No se pudieron guardar las franjas. Revisa los errores.');
        },
      });
  }

  /** 403 del back (prod, correo no autorizado): aviso claro, no un toast de error. */
  private avisoSinPermiso(texto: string): void {
    Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: texto,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#f59e0b',
    });
  }

  /**
   * 409 del back: otro operador guardó mientras se editaba. Mismo aviso que
   * el de sin permiso, con la opción de recargar (se pierde el borrador).
   */
  private async avisoConflicto(texto: string): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: texto,
      showCancelButton: true,
      confirmButtonText: 'Recargar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (r.isConfirmed && !this.usdt) this.cargar();
  }

  /** Toast corto arriba a la derecha, como en el resto del panel. */
  private aviso(icon: 'success' | 'warning' | 'error', title: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer: icon === 'success' ? 3500 : 5000,
      timerProgressBar: true,
    });
  }

  private limpiarResultados(): void {
    this.vista = null;
    this.vistaVieja = false;
    this.errores = [];
    this.advertencias = [];
    this.mensaje = null;
    this.mensajeAviso = false;
  }
}
